import {ref, reactive, computed, watch} from 'vue';
import GoToPageBuilder from "@/modules/reader/annotations/builders/goToPage/GoToPageBuilder";
import ToggleHintsBuilder from "@/modules/reader/annotations/builders/toggleHints/ToggleHintsBuilder";
import ToggleAllHintsBuilder from "@/modules/reader/annotations/builders/toggleHints/ToggleAllHintsBuilder";
import ContentRectBuilder from "@/modules/reader/annotations/builders/contentRect/ContentRectBuilder";
import {useDataLoader} from  "@/modules/reader/book/dataLoader";
import PlayKaraokeBuilder from "@/modules/reader/annotations/builders/karaoke/PlayKaraokeBuilder";
import PlayPageKaraokeBuilder from "@/modules/reader/annotations/builders/karaoke/PlayPageKaraokeBuilder";
import PlayMultiPageKaraokeBuilder from "@/modules/reader/annotations/builders/karaoke/PlayMultiPageKaraokeBuilder";
import PlayMultiPageKaraokeAnnotation  from "@/modules/reader/annotations/builders/karaoke/PlayMultiPageKaraokeAnnotation";
import PlayMultiPageBlockBuilder from "@/modules/reader/annotations/builders/karaoke/PlayMultiPageBlockBuilder";
import PlayMultiPageBlockAnnotation from "@/modules/reader/annotations/builders/karaoke/PlayMultiBlockBlockAnnotation";
import {useBookInfo} from "@/modules/reader/book/bookinfo";
import ShowResourceBuilder from "@/modules/reader/annotations/builders/showResource/ShowResourceBuilder";
import ShowResourceAnnotation from "@/modules/reader/annotations/builders/showResource/ShowResourceAnnotation";
import PlayBlockBuilder from "@/modules/reader/annotations/builders/karaoke/PlayBlockBuilder";
import PlaySpanBuilder from "@/modules/reader/annotations/builders/karaoke/PlaySpanBuilder";
import MuteRoleBuilder from "@/modules/reader/annotations/builders/karaoke/MuteRoleBuilder";
import StepByStepBuilder from "@/modules/reader/annotations/builders/stepByStep/StepByStepBuilder";
import MuteRoleAnnotation from "./builders/karaoke/MuteAnnotation";
import {useMuteRoleControl} from "@/modules/reader/karaoke/muteRoleControl";

const parserState = reactive({
    annotData:[]
})


export function useParser() {

    const {bookData} = useDataLoader();
    //const { bookInfoState} = useBookInfo();

    const readData = (pagenum) => {

        const bookPath = useBookInfo().bookInfoState.publicPath;
        const availableSpeech = useBookInfo().bookInfoState.availableSpeech;

        //if annotations have already been read, just skip the parsing process
        if (parserState.annotData[pagenum]) {
            return;
        }

        //just skip everything if page is out of range
        if (pagenum >=  bookData.annotations.length) {
            return;
        }

        console.log("reading data of page: " + pagenum)

        //get the targetRectId data and map with contentRect
        const toggleHintsIdMapping = [];

        //get the rect width of each karaoke row, used to avoid the row not being able to fully filled when completed, if the increment set in the audio data file is smaller that the rect width
        const karaokeRowWidth = {};

        parserState.annotData[pagenum] = {
            annotList:[], //store every raw rect area data from the pdf, so that they can be rendered in frontend
            annotTypes:[], //record what type of annotations the pages contain
            sharedMeta:[], //some additional data to be shared by more than one annotation, so that they can be accessed more conveniently
        };


        //grab the karaoke row widths first. The loop starts here since karaokeRowWidth will be needed to inject to the PlayKaraokeAnnotation
        for (let annotation of bookData.annotations[pagenum]) {

            let meta = annotation["meta"]
            let protocol = annotation["protocol"];
            let rect = annotation["rect"];

            if (annotation["raw_annot"]) {
                console.log(annotation["raw_annot"])
            }

            let result = resolveAnnotationSyntax(annotation["raw_annot"], meta);
            if (result) {
                protocol = result.protocol;
                meta = result.meta;
            }

            if (protocol === "contentRect") {

                if (meta.layer === "karaokeword") {

                    let karaokegrouptag = meta["karaokegrouptag"];
                    let rowindex = parseInt(meta["rowindex"])

                    if (!karaokeRowWidth[karaokegrouptag]) {
                        karaokeRowWidth[karaokegrouptag] = [];
                    }

                    karaokeRowWidth[karaokegrouptag][rowindex] = rect[2] - rect[0]
                }
            }
        }

        for (let annotation of  bookData.annotations[pagenum]) {

            let meta =  annotation["meta"]
            let protocol = annotation["protocol"];
            let rect = annotation["rect"];
            let builder;

            let result = resolveAnnotationSyntax(annotation["raw_annot"], meta);
            if (result) {
                protocol = result.protocol;
                meta = result.meta;
            }

            if (protocol === "gotoPage") {
                builder = new GoToPageBuilder.Builder()
                    .setRect(rect)
                    .setMeta(meta)
                    .build();

                setAnnotationsAndType(pagenum, builder);

            } else if (protocol === "showHints") {
                builder = new ToggleHintsBuilder.Builder()
                    .setRect(rect)
                    .setMeta(meta)
                    .build();

                let targetRectId = builder.getAnnotations().targetRectId;
                let grouptag = builder.getAnnotations().grouptag;
                let imgSrc = builder.getAnnotations().images;
                let showAsDefault = builder.getAnnotations().showAsDefault;

                toggleHintsIdMapping.push(
                    {
                        id: builder.getAnnotations().id,
                        targetRectId:targetRectId,
                        imgSrc: imgSrc,
                        grouptag: grouptag,
                        showAsDefault: (showAsDefault === 1) ? true : false
                    }
                );

                setAnnotationsAndType(pagenum, builder);

            } else if (protocol === "showResource") {
                builder = new ShowResourceBuilder.Builder()
                    .setRect(rect)
                    .setMeta(meta)
                    .build();

                setAnnotationsAndType(pagenum, builder);

                if (builder.getAnnotationTypes().includes(ShowResourceAnnotation.VIDEO)) {
                    if (builder.getAnnotations().targetRectId !== undefined){
                        if (!parserState.annotData[pagenum]["sharedMeta"]["inlineVideoId"]) {
                            parserState.annotData[pagenum]["sharedMeta"]["inlineVideoId"] = [];

                        }

                        parserState.annotData[pagenum]["sharedMeta"]["inlineVideoId"].push(builder.getAnnotations().targetRectId)
                    }
                }

            } else if (protocol === "playkaraoke") {
                builder = new PlayKaraokeBuilder.Builder()
                    .setRect(rect)
                    .setBookPath(bookPath)
                    .setKaraokeRowWidth(karaokeRowWidth)
                    .setAvailableSpeech(availableSpeech)
                    .setMeta(meta)
                    .build();


                setAnnotationsAndType(pagenum, builder);

                if (!parserState.annotData[pagenum]["sharedMeta"]["karaoke"]) {
                    parserState.annotData[pagenum]["sharedMeta"]["karaoke"] = {}
                }

                let grouptag = String(builder.getAnnotations()["grouptag"])
                let roleId = builder.getAnnotations().roleId;

                parserState.annotData[pagenum]["sharedMeta"]["karaoke"][grouptag] =
                    {
                        bgImage: builder.getAnnotations().bgImage,
                        wordAudioData: builder.getWordAudioData(),
                        audioId: builder.getAnnotations().audioId,
                        audioSrc: builder.getAnnotations().audioSrc,
                        grouptag:  builder.getAnnotations().grouptag,
                        id: builder.getAnnotations().id
                    }

                if (roleId) {
                    parserState.annotData[pagenum]["sharedMeta"]["karaoke"][grouptag].roleId = roleId;
                }

            } else if (protocol === "playpagekaraoke") {

                builder = new PlayPageKaraokeBuilder.Builder()
                    .setRect(rect)
                    .setBookPath(bookPath)
                    .setAvailableSpeech(availableSpeech)
                    .setMeta(meta)
                    .build();

                if (!parserState.annotData[pagenum]["sharedMeta"]["pageKaraokeAudioIds"]) {
                    parserState.annotData[pagenum]["sharedMeta"]["pageKaraokeAudioIds"] = {}
                }

                parserState.annotData[pagenum]["sharedMeta"]["pageKaraokeAudioIds"] = builder.getAnnotations().audioIds;
                setAnnotationsAndType(pagenum, builder);

            } else if (protocol === "playmultipagekaraoke") {

                builder = new PlayMultiPageKaraokeBuilder.Builder()
                    .setRect(rect)
                    .setPagenum(pagenum)
                    .setBookPath(bookPath)
                    .setAvailableSpeech(availableSpeech)
                    .setMeta(meta)
                    .build();

                setAnnotationsAndType(pagenum, builder);

            } else if (protocol === "playBlock") {

                builder = new PlayBlockBuilder.Builder()
                    .setRect(rect)
                    .setBookPath(bookPath)
                    .setAvailableSpeech(availableSpeech)
                    .setMeta(meta)
                    .build();

                setAnnotationsAndType(pagenum, builder);
            }
            else if (protocol === "playmultipageBlock") {

                builder = new PlayMultiPageBlockBuilder.Builder()
                    .setRect(rect)
                    .setBookPath(bookPath)
                    .setAvailableSpeech(availableSpeech)
                    .setMeta(meta)
                    .build();

                setAnnotationsAndType(pagenum, builder);

            }  else if (protocol === "playSpan") {

                builder = new PlaySpanBuilder.Builder()
                    .setRect(rect)
                    .setBookPath(bookPath)
                    .setAvailableSpeech(availableSpeech)
                    .setMeta(meta)
                    .build();

                setAnnotationsAndType(pagenum, builder);

                if (!parserState.annotData[pagenum]["sharedMeta"]["karaoke"]) {
                    parserState.annotData[pagenum]["sharedMeta"]["karaoke"] = {}
                }

            }  else if (protocol === "muteRole") {

                builder = new MuteRoleBuilder.Builder()
                    .setRect(rect)
                    .setBookPath(bookPath)
                    .setAvailableSpeech(availableSpeech)
                    .setMeta(meta)
                    .build();

                setAnnotationsAndType(pagenum, builder);

            } else if (protocol === "showNext" || protocol === "showPrevious") {

                builder = new StepByStepBuilder.Builder()
                    .setRect(rect)
                    .setMeta(meta)
                    .setProtocol(protocol)
                    .build();

                setAnnotationsAndType(pagenum, builder);

                let targetRectId = builder.getAnnotations().targetRectId;

                if (!parserState.annotData[pagenum]["sharedMeta"]["stepByStep"]) {
                    parserState.annotData[pagenum]["sharedMeta"]["stepByStep"] = {};
                }

                if (!parserState.annotData[pagenum]["sharedMeta"]["stepByStep"][targetRectId]) {
                    parserState.annotData[pagenum]["sharedMeta"]["stepByStep"][targetRectId] = {};
                }

                parserState.annotData[pagenum]["sharedMeta"]["stepByStep"][targetRectId]["imgSrcList"] = builder.getAnnotations().imgSrcList;

                //this will yield: either parserState.annotData[pagenum]["sharedMeta"][id]["showNext"] = true or  parserState.annotData[pagenum]["sharedMeta"][id]["showPrevious"] = true,
                //in this way, we'll know whether it's loop type or just one way step-by-step
                parserState.annotData[pagenum]["sharedMeta"]["stepByStep"][targetRectId][protocol] =  true;

            }
        }

        if (toggleHintsIdMapping.length) {
            parserState.annotData[pagenum]["sharedMeta"]["toggleHintsIdMapping"] = toggleHintsIdMapping;
        }

        //main annotation loop ended; the following further process the data needed, e.g some mapping data...
        for (let annotation of bookData.annotations[pagenum]) {

            let meta =  annotation["meta"]
            let protocol = annotation["protocol"];
            let rect = annotation["rect"];
            let builder;

            let result = resolveAnnotationSyntax(annotation["raw_annot"], meta);
            if (result) {
                protocol = result.protocol;
                meta = result.meta;
            }

            if (protocol === "contentRect") {

                builder = new ContentRectBuilder.Builder()
                    //.setRect(rect, (meta.layer && meta.layer === "showhide") ? true : false)
                    .setRect(rect, true)
                    .setMeta(meta)
                    .setToggleHintsMapping(toggleHintsIdMapping)
                    .build();

                setAnnotationsAndType(pagenum, builder);

            }  else if (protocol === "showAllHints") {

                builder = new ToggleAllHintsBuilder.Builder()
                    .setRect(rect)
                    .setMeta(meta)
                    .setToggleHintsMapping(toggleHintsIdMapping)
                    .build();

                setAnnotationsAndType(pagenum, builder);
             }
        }

        //make annotationTypes unique
        if (parserState.annotData[pagenum]["annotTypes"].length) {
            parserState.annotData[pagenum]["annotTypes"] = [... new Set( parserState.annotData[pagenum]["annotTypes"])];
        }

        //attempt to make the individual karaoke audioId order sync with playPageKaraoke audioIds (if exists),
        //since it's not guaranteed that the playKaraoke annotation is in the same order as the playPageKaraoke audioIds order:
        if (parserState.annotData[pagenum]["sharedMeta"]["pageKaraokeAudioIds"]) {
            let reOrderedKaraokeData = {};
            for (let audioId of parserState.annotData[pagenum]["sharedMeta"]["pageKaraokeAudioIds"]) {
                for (let [key,value] of Object.entries(parserState.annotData[pagenum]["sharedMeta"]["karaoke"])) {
                    if (value.audioId === audioId) {
                        reOrderedKaraokeData[key] = value;
                        break;
                    }
                }
            }

            parserState.annotData[pagenum]["sharedMeta"]["karaoke"] = reOrderedKaraokeData;

        }

        //Further process data if multiPage Karaoke annotations exist:
        if (parserState.annotData[pagenum]["annotTypes"].indexOf(PlayMultiPageKaraokeAnnotation.TYPE) ||
            parserState.annotData[pagenum]["annotTypes"].indexOf(PlayMultiPageBlockAnnotation.TYPE)
        ) {
            readMultiPageKaraokeOrBlockNextPage(parserState.annotData[pagenum]["annotList"], pagenum)
        }

        //remove mute role page ranges if mute role annotation is not detected in current page:
        updateMuteRoleRanges(pagenum, parserState.annotData[pagenum]["annotTypes"]);

        console.log("@parser  useMuteRoleControl().muteRoleState.pageRanges:");
        console.log( useMuteRoleControl().muteRoleState.pageRanges)

        console.log("@parser annoData")
        console.log(parserState.annotData)

    }


    /**
     *  If there is multiPageKaraoke annotation, read the remaining pages in range.
     * @param annotList
     */
    const readMultiPageKaraokeOrBlockNextPage = (annotList, pagenum)=> {

        let muteRolePageRange = [];
        let preloadPageRange = [];

        for (let item of annotList) {
            if (item.isPlayMultiPageKaraokeBtn || item.isPlayMultiPageBlockBtn) {
                let startPageIndex = Math.min(...item.pageRanges);
                let endPageIndex = Math.max(...item.pageRanges);

                for (let i = startPageIndex; i <= endPageIndex; i++) {
                    preloadPageRange.push(i);
                    muteRolePageRange.push(i);
                }

                //if the end page is an even number (zero-based), also get the next page to facilitate double paging
                if (endPageIndex % 2 === 0) {
                    endPageIndex++;
                }

                for (let i = startPageIndex; i <= endPageIndex; i++) {
                    preloadPageRange.push(i)
                }

            }

            if (item.isPlayKaraokeBtn) {
                if (item.aimPageIndex !== undefined) {
                    preloadPageRange.push(item.aimPageIndex);
                    muteRolePageRange.push(item.aimPageIndex);

                    //if aimPageIndex is an even number (zero-based), also get the next page to facilitate double paging
                    if (item.aimPageIndex % 2 === 0) {
                        preloadPageRange.push(item.aimPageIndex + 1);
                    }
                }
            }
        }

        muteRolePageRange = [... new Set(muteRolePageRange.sort())];
        preloadPageRange = [... new Set(preloadPageRange.sort())];

        for (let item of muteRolePageRange) {
            useMuteRoleControl().muteRoleState.pageRanges[item] = muteRolePageRange;
        }

        for (let item of preloadPageRange) {
            if (item !== pagenum) {
                readData(item);
            }
        }

        console.log('useMuteRoleControl().muteRoleState.pageRanges:')
        console.log(useMuteRoleControl().muteRoleState.pageRanges)
    }

    /**
     * Remove mute role page ranges if mute role annotation is not detected in current page.
     * Since mute role ranges only tell the related multi karaoke/blocks pages
     * it doesn't know whether mute role annotation exists or not, until all annotations of the related pages have been parsed.
     * Call this method at the end of parsing annotations, so that muteRoleState.pageRanges data is stored for those pages having mute roles only.
     * @param pagenum
     * @param annotationTypes
     */
    const updateMuteRoleRanges = (pagenum, annotationTypes)=> {

        if (!annotationTypes.includes(MuteRoleAnnotation.TYPE)) {
            if (useMuteRoleControl().muteRoleState.pageRanges[pagenum]) {
                useMuteRoleControl().muteRoleState.pageRanges[pagenum] = null;
            }
        }
    }


    const setAnnotationsAndType = (pagenum, builder) => {
        parserState.annotData[pagenum]["annotList"].push(builder.getAnnotations());
        parserState.annotData[pagenum]["annotTypes"] =  parserState.annotData[pagenum]["annotTypes"].concat(builder.getAnnotationTypes());
    }

    /**
     * Dirty method to remove leading http:// in raw annotation.
     * Please check in the pdf authoring phase, this method is expected to be removed when the annotations are correct in syntax.
     * Example: http://contentRect://karaokegrouptag=0|rowindex=0|layer=karaokeword
     * appeared @pripth_5bu2tb, p26
     * @param rawAnnotation
     */
    const resolveAnnotationSyntax = (rawAnnotation, meta)=>{

        let result = {};
        if (rawAnnotation) {
            let parts = rawAnnotation.split("://");
            //if rawAnnotation starts with the string 'http://', remove it!
            if (parts.length > 2 && rawAnnotation.indexOf("http://") === 0) {
                result.protocol = parts[1];
            } else {
                return;
            }
        } else {
            return;
        }

        for (let prop in meta) {
            let parts = prop.split("://");
            if (parts.length>1) {
                let revisedProp = prop.split("://")[1];
                let restoredValue = meta[prop];
                meta[revisedProp] = restoredValue;
                delete meta[[prop]];
            }
        }

        result.meta = meta;
        return result;
    }

    const reset = ()=>{
        parserState.annotData = [];
    }

    return {
        reset,
        bookData,
        parserState,
        readData
    }
}

